import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { updateObjective } from '../../store/map3PendingSlice';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import { ObjectiveType, KpiSettings } from '@mm-shared/types/Objective1';
import { validateKpiSettings, getDefaultKpiSettings } from '../../utils/kpiUtils';

export function SettingsTab() {
  const [searchParams] = useSearchParams();
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const dispatch = useDispatch<AppDispatch>();

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // Local state for form
  const [objectiveType, setObjectiveType] = useState<ObjectiveType>(
    objective?.type || 'deliverable',
  );
  const [kpiSettings, setKpiSettings] = useState<KpiSettings>(
    objective?.kpiSettings || getDefaultKpiSettings('%', true),
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize form when objective changes (navigation only)
  useEffect(() => {
    if (objective) {
      setObjectiveType(objective.type || 'deliverable');
      setKpiSettings(objective.kpiSettings || getDefaultKpiSettings('%', true));
    }
  }, [objective]);

  if (!objective) {
    return <div className="p-4 text-gray-500">Objective not found</div>;
  }

  const handleTypeChange = (newType: ObjectiveType) => {
    setObjectiveType(newType);
    setHasUnsavedChanges(true);
  };

  const handleNumberTypeChange = (numberType: '%' | '#') => {
    setKpiSettings((prev) => ({
      ...prev,
      numberType,
      ranges: getDefaultKpiSettings(numberType, prev.higherIsBetter).ranges,
    }));
    setHasUnsavedChanges(true);
  };

  const handleHigherIsBetterChange = (higherIsBetter: boolean) => {
    setKpiSettings((prev) => {
      const newRanges = getDefaultKpiSettings(prev.numberType, higherIsBetter).ranges;
      return {
        ...prev,
        higherIsBetter,
        ranges: newRanges,
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleRangeChange = (field: keyof KpiSettings['ranges'], value: number) => {
    setKpiSettings((prev) => ({
      ...prev,
      ranges: {
        ...prev.ranges,
        [field]: value,
      },
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!canEdit) {
      console.warn('Save denied: user does not have edit permissions');
      return;
    }

    // Validate KPI settings if metric type
    if (objectiveType === 'metric') {
      const validation = validateKpiSettings(kpiSettings);
      if (!validation.valid) {
        setValidationErrors(validation.errors);
        return;
      }
    }

    setValidationErrors([]);

    // Prepare updates
    const updates: Partial<typeof objective> = {
      type: objectiveType,
    };

    // Only include kpiSettings if metric type
    if (objectiveType === 'metric') {
      updates.kpiSettings = kpiSettings;
    }

    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates,
      }),
    );

    setHasUnsavedChanges(false);
  };

  const handleCancel = () => {
    setObjectiveType(objective.type || 'deliverable');
    setKpiSettings(objective.kpiSettings || getDefaultKpiSettings('%', true));
    setValidationErrors([]);
    setHasUnsavedChanges(false);
  };

  return (
    <div className="space-y-6 p-4">
      {/* Objective Type Selection */}
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Success will be measured by:
        </label>
        <div className="mt-2 space-y-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="objectiveType"
              value="deliverable"
              checked={objectiveType === 'deliverable'}
              onChange={() => handleTypeChange('deliverable')}
              disabled={!canEdit}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Deliverables</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Track completion status (green/yellow/red)
              </span>
            </span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="objectiveType"
              value="metric"
              checked={objectiveType === 'metric'}
              onChange={() => handleTypeChange('metric')}
              disabled={!canEdit}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Metrics</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Track performance metrics with continuous color scale
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* KPI Settings Form - Only shown for metric type */}
      {objectiveType === 'metric' && (
        <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Metric Configuration
          </h3>

          {/* Number Type */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Number Type
            </label>
            <div className="mt-2 flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="numberType"
                  value="%"
                  checked={kpiSettings.numberType === '%'}
                  onChange={() => handleNumberTypeChange('%')}
                  disabled={!canEdit}
                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Percentage (%)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="numberType"
                  value="#"
                  checked={kpiSettings.numberType === '#'}
                  onChange={() => handleNumberTypeChange('#')}
                  disabled={!canEdit}
                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Real Number (#)</span>
              </label>
            </div>
          </div>

          {/* Higher is Better */}
          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={kpiSettings.higherIsBetter}
                onChange={(e) => handleHigherIsBetterChange(e.target.checked)}
                disabled={!canEdit}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 disabled:opacity-50"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Higher values are better
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {kpiSettings.higherIsBetter
                ? 'Best: highest value, Worst: lowest value'
                : 'Best: lowest value, Worst: highest value'}
            </p>
          </div>

          {/* Threshold Ranges */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Performance Thresholds
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Best Green (g22)
                </label>
                <input
                  type="number"
                  value={kpiSettings.ranges.bestGreen}
                  onChange={(e) => handleRangeChange('bestGreen', Number(e.target.value))}
                  disabled={!canEdit}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 text-sm dark:bg-zinc-700 dark:border-gray-600 dark:text-white"
                  step={kpiSettings.numberType === '%' ? 1 : 0.1}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Best Yellow (y12)
                </label>
                <input
                  type="number"
                  value={kpiSettings.ranges.bestYellow}
                  onChange={(e) => handleRangeChange('bestYellow', Number(e.target.value))}
                  disabled={!canEdit}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 text-sm dark:bg-zinc-700 dark:border-gray-600 dark:text-white"
                  step={kpiSettings.numberType === '%' ? 1 : 0.1}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Worst Yellow (y7)
                </label>
                <input
                  type="number"
                  value={kpiSettings.ranges.worstYellow}
                  onChange={(e) => handleRangeChange('worstYellow', Number(e.target.value))}
                  disabled={!canEdit}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 text-sm dark:bg-zinc-700 dark:border-gray-600 dark:text-white"
                  step={kpiSettings.numberType === '%' ? 1 : 0.1}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Worst Red (r1)
                </label>
                <input
                  type="number"
                  value={kpiSettings.ranges.worstRed}
                  onChange={(e) => handleRangeChange('worstRed', Number(e.target.value))}
                  disabled={!canEdit}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 text-sm dark:bg-zinc-700 dark:border-gray-600 dark:text-white"
                  step={kpiSettings.numberType === '%' ? 1 : 0.1}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {kpiSettings.higherIsBetter
                ? 'Thresholds should be in descending order (best → worst)'
                : 'Thresholds should be in ascending order (best → worst)'}
            </p>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3">
              <div className="text-sm text-red-800 dark:text-red-200">
                <p className="font-medium mb-1">Please fix the following errors:</p>
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {canEdit && hasUnsavedChanges && (
        <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-zinc-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-zinc-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Save Settings
          </button>
        </div>
      )}

      {!canEdit && (
        <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            You don't have permission to edit these settings.
          </p>
        </div>
      )}
    </div>
  );
}
